import {StyledChip, StyledProPhotoGrid, StyledSetCoverChip, StyledTooltipText} from './style'
import {RemapExperienceVetting} from '../../types'
import {Button} from '@/components/ui/button/Button'
import {
    GridLayout01Icon,
    GridLayout02Icon,
    GridLayout03Icon,
    GridLayout04Icon,
    MoveIcon
} from '@/components/ui/icon/Icon'
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    useSensors,
    useSensor,
    DragStartEvent
} from '@dnd-kit/core'
import {SortableContext, arrayMove, rectSortingStrategy} from '@dnd-kit/sortable'
import {ExpVettingCriteriaGalleryUploadingItem} from '../exp-vetting-criteria-gallery-uploading-item/ExpVettingCriteriaGalleryUploadingItem'
import {useEffect, useState} from 'react'
import {useUpdateExperienceVettingGalleryImagesPositions} from '../../queries/useUpdateExperienceVettingGalleryImagesPositions'
import {useGalleryNeedsReviewShortcuts} from '@/features/photoset/hooks/useGalleryNeedsReviewShortcuts'
import {ExperienceGalleryImage} from '@/features/experience-shell/types'
import {GalleryImageItem} from '@/components/commons/gallery-image-item/GalleryImageItem'
import {useExperienceVettingPhotoUpload} from '@/features/service-vetting/store/servicesPhotoUpload'
import {GalleryModal} from '@/components/commons/gallery-modal/GalleryModal'
import {GalleryModalHeader} from '@/components/commons/gallery-modal-header/GalleryModalHeader'
import {GalleryModalThumbnails} from '@/components/commons/gallery-modal-thumbnails/GalleryModalThumbnails'
import {ExperienceVettingCriteriaUploadButton} from '../experience-vetting-criteria-upload-button/ExperienceVettingCriteriaUploadButton'
import {ProPhotosEmptyGalleryState} from '@/features/submission-pro-photos-upload/components/pro-photos-empty-gallery-state/ProPhotosEmptyGalleryState'
import {ProPhotosDeleteImageModal} from '@/features/submission-pro-photos-upload/components/pro-photos-delete-image-modal/ProPhotosDeleteImageModal'
import {useDialogStore} from '@ariakit/react'
import {useDeleteExperienceVettingPhotos} from '../../queries/useDeleteExperienceVettingPhotos'
import {ProPhotosGalleryItem} from '@/features/submission-pro-photos-upload/components/pro-photos-gallery-item/ProPhotosGalleryItem'
import {useTranslation} from '@/translations/i18n'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Chip} from '@/components/commons/chip/Chip'
import {useUpdateExperiencVettingCoverPhoto} from '../../queries/useUpdateExperiencVettingCoverPhoto'
import {Spinner} from '@/components/ui/spinner/Spinner'

export const MINIMUM_IMAGES_REQUIRED = 5
const GRID_LAYOUT_ICONS = [GridLayout01Icon, GridLayout02Icon, GridLayout03Icon, GridLayout04Icon]

export const ExpProPhotosGrid = ({experience}: {experience: RemapExperienceVetting}) => {
    const {t} = useTranslation()
    const {gallery} = experience
    const [activeId, setActiveId] = useState<number | string | null>(null)
    const [orderedImages, setOrderedImages] = useState(gallery.images)
    const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(null)
    const [showGalleryModal, setShowGalleryModal] = useState(false)
    const {galleryItems: uploadingItems} = useExperienceVettingPhotoUpload()
    const deleteImageStore = useDialogStore()
    const [imageToDelete, setImageToDelete] = useState<ExperienceGalleryImage | null>(null)

    const updateExperienceVettingGalleryImagesPositions = useUpdateExperienceVettingGalleryImagesPositions({
        experienceId: experience.id,
        galleryId: experience.gallery.id
    })
    const updateExperienceCoverPhotoMutation = useUpdateExperiencVettingCoverPhoto({
        experienceId: experience.id,
        galleryId: experience.gallery.id
    })

    const handleChangeCoverPhoto = (id: number) => {
        updateExperienceCoverPhotoMutation.mutate({isCover: true, imageId: id})
    }

    useEffect(() => {
        setOrderedImages(gallery.images)
    }, [gallery.images])

    const onCloseDeletePhotoModal = () => {
        setImageToDelete(null)
        deleteImageStore.toggle()
    }

    const deletePhotoMutation = useDeleteExperienceVettingPhotos(
        {galleryId: experience.gallery.id, experienceId: experience.id},
        {onSuccess: onCloseDeletePhotoModal}
    )

    const onClickDeletePhoto = (e: React.MouseEvent<HTMLButtonElement>, imageId: ExperienceGalleryImage['id']) => {
        e.stopPropagation()
        deletePhotoMutation.mutate({imageIds: [imageId]})
    }

    const handleCloseModal = () => {
        setExpandedImageIndex(null)
        setShowGalleryModal(false)
    }

    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id)
    }
    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event

        if (!over) {
            setActiveId(null)
            return
        }

        const oldIndex = orderedImages.findIndex(image => image.id === active.id)
        const newIndex = orderedImages.findIndex(image => image.id === over.id)

        if (oldIndex !== newIndex) {
            // Update local state immediately for responsive UI
            const newOrderedImages = arrayMove([...orderedImages], oldIndex, newIndex)
            setOrderedImages(newOrderedImages)

            updateExperienceVettingGalleryImagesPositions.mutate(
                newOrderedImages.map((image, index) => ({id: image.id, position: index + 1}))
            )
        }

        setActiveId(null)
    }
    const handleDragCancel = () => {
        setActiveId(null)
    }

    const handleClickExpandedImage = (imageId: ExperienceGalleryImage['id']) => {
        const index = orderedImages.findIndex(image => image.id === imageId)
        setExpandedImageIndex(index)
        setShowGalleryModal(true)
    }

    useGalleryNeedsReviewShortcuts({
        selectableImages: orderedImages,
        currentDetailImageIndex: expandedImageIndex || 0,
        setCurrentDetailImageIndex: setExpandedImageIndex,
        onClose: handleCloseModal
    })

    return (
        <>
            {experience.gallery.images.length > 0 ? (
                <StyledProPhotoGrid>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <SortableContext items={orderedImages} strategy={rectSortingStrategy}>
                            {orderedImages.map((image, index) => {
                                const GridLayoutIcon = GRID_LAYOUT_ICONS[index]
                                return (
                                    <ProPhotosGalleryItem
                                        key={image.id}
                                        imageUrl={image.thumbnails?.xs || image.url}
                                        imageName={image.name}
                                        bottomLeftSlot={
                                            <Flexbox gap={1} align="center" wrap="wrap">
                                                {index < 4 && (
                                                    <Tooltip
                                                        maxWidth={'200px'}
                                                        isOpenOnClickEnabled={false}
                                                        content={
                                                            <StyledTooltipText>
                                                                <p>
                                                                    {t(
                                                                        'vetting:reordering:experience:placement_on_the_pdp_grid'
                                                                    )}
                                                                </p>
                                                                <span>
                                                                    {t(
                                                                        'vetting:reordering:experience:drag_and_drop_to_change_placement'
                                                                    )}
                                                                </span>
                                                            </StyledTooltipText>
                                                        }
                                                    >
                                                        <StyledChip
                                                            label={t('vetting:reordering:grid', {
                                                                number: index + 1
                                                            })}
                                                            variant={'neutral'}
                                                            shape="roundedRectangle"
                                                            endSlot={
                                                                <GridLayoutIcon
                                                                    width={16}
                                                                    height={16}
                                                                    viewBox="0 0 16 16"
                                                                />
                                                            }
                                                        />
                                                    </Tooltip>
                                                )}

                                                {image.isCover ? (
                                                    <Chip
                                                        label={t('vetting:reordering:cover')}
                                                        variant={'neutral'}
                                                        shape="roundedRectangle"
                                                    />
                                                ) : (
                                                    <StyledSetCoverChip
                                                        className={'show-on-hover'}
                                                        label={t('vetting:reordering:set_as_cover')}
                                                        variant={'neutral'}
                                                        shape="roundedRectangle"
                                                        endSlot={
                                                            updateExperienceCoverPhotoMutation.isPending && <Spinner />
                                                        }
                                                        onClick={() =>
                                                            !updateExperienceCoverPhotoMutation.isPending &&
                                                            handleChangeCoverPhoto(image.id)
                                                        }
                                                    />
                                                )}
                                            </Flexbox>
                                        }
                                        isPendingImage={image.isPending ?? false}
                                        imageId={image.id}
                                        isHostApprovalNeeded={image.isHostApprovalNeeded}
                                        isProPhotography={image.isProPhotography ?? false}
                                        onDeleteModalOpen={() => {
                                            setImageToDelete(image)
                                            deleteImageStore.toggle()
                                        }}
                                        onClickExpand={() => handleClickExpandedImage(image.id)}
                                    />
                                )
                            })}
                        </SortableContext>
                        <DragOverlay style={{transformOrigin: '0 0'}}>
                            {activeId ? (
                                <GalleryImageItem
                                    style={{
                                        cursor: 'grabbing'
                                    }}
                                    src={
                                        orderedImages.find(image => image.id === activeId)?.thumbnails?.xs ||
                                        orderedImages.find(image => image.id === activeId)?.url ||
                                        ''
                                    }
                                    topLeftSlot={
                                        <Button variant="secondary" iconOnly className="show-on-hover" size="sm">
                                            <MoveIcon size={16} />
                                        </Button>
                                    }
                                />
                            ) : null}
                        </DragOverlay>
                    </DndContext>

                    {uploadingItems.map(image => (
                        <ExpVettingCriteriaGalleryUploadingItem key={image.id} image={image} hideAction={false} />
                    ))}
                </StyledProPhotoGrid>
            ) : (
                <ProPhotosEmptyGalleryState
                    minimumImagesRequired={MINIMUM_IMAGES_REQUIRED}
                    uploadButton={
                        <ExperienceVettingCriteriaUploadButton
                            experienceId={experience.id}
                            galleryId={experience.gallery.id}
                            buttonText="vetting:reordering:upload_from_device"
                            buttonProps={{
                                variant: 'tertiary',
                                size: 'sm'
                            }}
                            withIcon={false}
                        />
                    }
                />
            )}

            {showGalleryModal && expandedImageIndex != null && (
                <GalleryModal
                    currentImage={orderedImages[expandedImageIndex]}
                    thumbnails={{
                        l: orderedImages[expandedImageIndex].url || '',
                        m: '',
                        s: '',
                        xs: ''
                    }}
                    onClose={handleCloseModal}
                    headerSlot={
                        <GalleryModalHeader
                            currentImage={orderedImages[expandedImageIndex]}
                            imagesCount={orderedImages.length}
                            onClose={handleCloseModal}
                        />
                    }
                    thumbnailsSlot={
                        <GalleryModalThumbnails
                            currentImageId={orderedImages[expandedImageIndex].id}
                            thumbnails="thumbnails"
                            images={orderedImages.map(image => ({
                                id: image.id,
                                name: image.name,
                                thumbnails: {
                                    l: image.url || '',
                                    m: image.url || '',
                                    s: image.url || '',
                                    xs: image.url || ''
                                }
                            }))}
                            onSelect={(_, index) => {
                                setExpandedImageIndex(index)
                            }}
                        />
                    }
                />
            )}

            {imageToDelete != null && (
                <ProPhotosDeleteImageModal
                    store={deleteImageStore}
                    onDelete={e => onClickDeletePhoto(e, imageToDelete.id)}
                    onClose={onCloseDeletePhotoModal}
                    isPending={deletePhotoMutation.isPending}
                />
            )}
        </>
    )
}
